import { useCallback } from 'react';
import {
    alertApiRef,
    discoveryApiRef,
    fetchApiRef,
    useApi,
} from '@backstage/core-plugin-api';

/**
 * Asks the CmdbEntityProvider to re-read the CMDB.
 *
 * The catalog's own entity refresh only re-processes what the provider stored
 * during its last run, so for CMDB entities it never picks up new data - this
 * is what actually makes the refresh button do something. The backend runs the
 * provider at most once a minute and answers 429 in between.
 *
 * What it tells the reader says "refresh" and not which source: the button is
 * the refresh of the entity, and the CMDB is not the only source it is meant
 * to re-read for long.
 */
export function useCmdbRefresh(): () => Promise<void> {
    const discoveryApi = useApi(discoveryApiRef);
    const fetchApi = useApi(fetchApiRef);
    const alertApi = useApi(alertApiRef);

    return useCallback(async () => {
        const baseUrl = await discoveryApi.getBaseUrl('catalog');
        const response = await fetchApi.fetch(`${baseUrl}/rtl/cmdb/refresh`, {
            method: 'POST',
        });

        if (response.status === 429) {
            alertApi.post({
                message:
                    'A refresh was started less than a minute ago, please try again shortly',
                severity: 'info',
                display: 'transient',
            });
            return;
        }

        if (!response.ok) {
            throw new Error(`Refresh failed with status ${response.status}`);
        }

        alertApi.post({
            message: 'Refresh started, the data follows in a moment',
            severity: 'info',
            display: 'transient',
        });
    }, [discoveryApi, fetchApi, alertApi]);
}
